import { Component, DoCheck, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DATE_FORMAT } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxNumberBoxModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import {
    AccessType,
    AuthService,
    DataSources,
    FinancialDocumentDto,
    FinancialDocumentItemDto,
    PermissionType,
} from '@services';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import DataSource from 'devextreme/data/data_source';

@Component({
    selector: 'app-invoice-details-items',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        DxNumberBoxModule,
    ],
    templateUrl: './invoice-details-items.component.html',
})
export class InvoiceDetailsItemsComponent implements OnInit, DoCheck {
    @Input() invoice!: FinancialDocumentDto;
    @Input() isReadOnly: boolean = false;
    @Input() permissionType: PermissionType | undefined = undefined;
    @Input() negativeAmountsOnly: boolean = false;

    private dataSources = inject(DataSources);
    private authService = inject(AuthService);

    siteDataSource = this.dataSources.siteOptions();
    private glCodeStore = this.dataSources.glCodeOptions();
    private glCodeDataSources = new WeakMap<FinancialDocumentItemDto, { siteId: string; dataSource: DataSource }>();

    public dateFormat = DATE_FORMAT;
    public isEditingConversionRate = false;
    public editingGlCodeIndexes = new Set<number>();

    public taxOptions = [
        { name: '0%', rate: 0 },
        { name: '5%', rate: 0.05 },
        { name: '13%', rate: 0.13 },
    ];

    private previousConversionRate?: number;

    async ngOnInit() {
        this.siteDataSource = this.dataSources.siteOptions(this.permissionType ?? null);
        this.glCodeStore = this.dataSources.glCodeOptions(this.permissionType ?? null);
        this.previousConversionRate = this.invoice?.conversionRate;
        this.calculateTotalAmount();
    }

    ngDoCheck(): void {
        if (this.invoice?.conversionRate !== this.previousConversionRate) {
            this.previousConversionRate = this.invoice?.conversionRate;
            this.calculateTotalAmount();
        }
    }

    addAction() {
        const lastItem = this.invoice.items?.length
            ? this.invoice.items[this.invoice.items.length - 1]
            : undefined;

        this.invoice.items!.push(
            FinancialDocumentItemDto.fromJS({
                quantity: 1,
                description: '',
                unitPrice: 0,
                taxRate: 0,
                glCodeName: lastItem?.glCodeName,
                glCodeId: lastItem?.glCodeId,
                siteId: lastItem?.siteId,
            }),
        );

        this.calculateTotalAmount();
        this.scrollToSection('itemsSection');
    }

    removeAction(index: number) {
        this.invoice.items!.splice(index, 1);
        this.editingGlCodeIndexes.clear();
        this.calculateTotalAmount();
    }

    toggleGlCodeEdit(index: number) {
        if (this.editingGlCodeIndexes.has(index)) {
            this.editingGlCodeIndexes.delete(index);
        } else {
            this.editingGlCodeIndexes.add(index);
        }
    }

    get canSeeGlCodes() {
        return this.permissionType
            ? this.authService.checkPermission(this.permissionType, AccessType.ViewOnly) 
            : false;
    }

    get canEditGlCodes() {
        return this.permissionType
            ? this.authService.checkPermission(this.permissionType, AccessType.Edit) 
            : false;
    }

    glCodeDataSourceFor(item: FinancialDocumentItemDto): DataSource | null {
        if (!item.siteId) return null;

        const cached = this.glCodeDataSources.get(item);
        if (cached?.siteId === item.siteId) return cached.dataSource;

        const dataSource = new DataSource({
            store: this.glCodeStore,
            filter: ['clientId', '=', item.siteId],
            paginate: true,
        });
        this.glCodeDataSources.set(item, { siteId: item.siteId, dataSource });
        return dataSource;
    }

    onSiteChanged(event: { event?: unknown }, item: FinancialDocumentItemDto) {
        if (event.event) {
            item.glCodeId = undefined;
            item.glCodeName = undefined;
        }
    }

    onGlCodeChanged(
        event: { selectedItem?: { id?: string; name?: string } },
        item: FinancialDocumentItemDto,
    ) {
        item.glCodeName = event.selectedItem?.name;
    }

    onUnitPriceChanged(event: { value?: number | null }, item: FinancialDocumentItemDto) {
        if (this.negativeAmountsOnly && event.value != null && event.value > 0) {
            item.unitPrice = -event.value;
        }

        this.calculateTotalAmount();
    }

    calculateTotalAmount() {
        if (!this.invoice?.items) return;

        let subTotal = 0;
        let taxAmount = 0;

        this.invoice.items.forEach((item) => {
            const qty = item.quantity ?? 0;
            const price = item.unitPrice ?? 0;
            const rate = item.taxRate ?? 0;

            subTotal += qty * price;
            taxAmount += qty * price * rate;
        });

        this.invoice.subTotal = subTotal;
        this.invoice.taxAmount = taxAmount;
        this.invoice.totalAmount = subTotal + taxAmount;

        if (this.invoice.conversionRate) {
            const useRate = this.invoice.conversionRate > 0;

            this.invoice.convertedSubTotal = useRate
                ? this.invoice.subTotal * this.invoice.conversionRate
                : this.invoice.subTotal;
            this.invoice.convertedTaxAmount = useRate
                ? this.invoice.taxAmount * this.invoice.conversionRate
                : this.invoice.taxAmount;
            this.invoice.convertedTotalAmount = useRate
                ? this.invoice.totalAmount * this.invoice.conversionRate
                : this.invoice.totalAmount;
        }
    }

    scrollToSection(sectionId: string): void {
        const el = document.getElementById(sectionId);

        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'end' });

            setTimeout(() => {
                window.scrollBy({ top: 50, behavior: 'smooth' });
            }, 200);
        }
    }
}
